/**
 * veriAyarlariService.js — Sistem Ayarları › Veri Ayarları: firma türleri (#159) ve dosya türleri (#158)
 *
 * Firma türleri:  GET /firma/types/yonetim (yalnızca personel) · POST /firma/types · PUT /firma/types/{id}
 *                 · DELETE /firma/types/{id}  (yazma uçları da yalnızca personel, #154)
 * Dosya türleri:  GET /file-types/yonetim · POST /file-types · PUT /file-types/{id} · DELETE /file-types/{id}
 *                 (FileTypeController, yalnızca personel). Seçiciler GET /document-templates/file-types'te kalıyor.
 *
 * Kurallar backend'de (FirmaTypeConstant.KODDA_KULLANILAN / FileTypeConstant.KODDA_KULLANILAN): kodun id ile
 * okuduğu türler yeniden adlandırılır ama taşınmaz / silinmez; kullanımda olan tür silinmez. Sayfa
 * `koddaKullanilan` ve `silinebilir` bayraklarını sunucudan okur, kendisi türetmez.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

const FIRMA_API = `${JAVA_BACKEND_API_URL}/firma`;
const FILE_API = `${JAVA_BACKEND_API_URL}/file-types`;

/** Dil kimlikleri (language tablosu): 1 Türkçe, 2 İngilizce. */
export const DIL = { TR: 1, EN: 2 };

function parseError(error) {
  const data = error.response?.data;
  const message =
    data?.message ||
    (typeof data === 'string' && data ? data : null) ||
    error.message ||
    i18n.t("services:veriAyarlariService.bilinmeyenHata");
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  throw wrapped;
}

/** GenericResultJson `{result: false, message}` 200 ile dönerse de hata say. */
function sonuc(response) {
  const data = response.data;
  if (data && data.result === false) {
    const error = new Error(data.message || i18n.t("services:veriAyarlariService.islemYapilamadi"));
    error.status = response.status;
    throw error;
  }
  return data;
}

// ── Firma türleri (#159) ──────────────────────────────────────────────────────────────────────

/**
 * [{ firmaTypeId, firmaTypeValue, parentId, parentValue, adTr, adEn, firmaToFuar, firmaToStand,
 *    firmaTaseron, atamaTalebi, isKazasiFormu, fuarBelgeTipi, kisiBelgeIzni, altTipSayisi,
 *    koddaKullanilan, taseronSayilir, kullanim, silinebilir }]
 */
export async function getFirmaTurleri() {
  try {
    const response = await axios.get(`${FIRMA_API}/types/yonetim`, { headers: getAuthHeader() });
    return response.data || [];
  } catch (error) {
    return parseError(error);
  }
}

function firmaTuruGovdesi({ firmaTypeValue, parentId, adTr, adEn }) {
  const texts = [];
  if (adTr != null && adTr.trim()) texts.push({ languageId: DIL.TR, firmaTypeName: adTr.trim() });
  if (adEn != null && adEn.trim()) texts.push({ languageId: DIL.EN, firmaTypeName: adEn.trim() });
  return { firmaTypeValue: firmaTypeValue?.trim(), parentId: Number(parentId) || 0, texts };
}

export async function createFirmaTuru(form) {
  try {
    return sonuc(await axios.post(`${FIRMA_API}/types`, firmaTuruGovdesi(form), { headers: getAuthHeader() }));
  } catch (error) {
    return parseError(error);
  }
}

export async function updateFirmaTuru(id, form) {
  try {
    return sonuc(await axios.put(`${FIRMA_API}/types/${id}`, firmaTuruGovdesi(form), { headers: getAuthHeader() }));
  } catch (error) {
    return parseError(error);
  }
}

export async function deleteFirmaTuru(id) {
  try {
    return sonuc(await axios.delete(`${FIRMA_API}/types/${id}`, { headers: getAuthHeader() }));
  } catch (error) {
    return parseError(error);
  }
}

// ── Çeviriler (#337) ──────────────────────────────────────────────────────────────────────────

/**
 * Her çevrilen katalog için: personelin gördüğü satır sayısı ve İngilizcesi olmayanlar.
 * [{ katalog, toplam, eksik, ceviriliYuzde }] — en büyük açık önce. GET /ceviri/eksikler (yalnızca personel).
 */
export async function getEksikCeviriler() {
  try {
    const response = await axios.get(`${JAVA_BACKEND_API_URL}/ceviri/eksikler`, { headers: getAuthHeader() });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    return parseError(error);
  }
}

// ── Dosya türleri (#158) ──────────────────────────────────────────────────────────────────────

/**
 * [{ fileTypeId, fileTypeName, extra, parentId, parentName, isImage, activeFuarNeeded, adTr, adEn, dosya,
 *    firmaBelgesi, standFotografi, projeVeSalonDosyasi, kural, tasimaGecmisi, kisiBelgeIzni, altTurSayisi,
 *    koddaKullanilan, kullanim, silinebilir }]
 */
export async function getDosyaTurleri() {
  try {
    const response = await axios.get(`${FILE_API}/yonetim`, { headers: getAuthHeader() });
    return response.data || [];
  } catch (error) {
    return parseError(error);
  }
}

function dosyaTuruGovdesi({ name, adEn, extra, parentId, isImage, activeFuarNeeded }) {
  return {
    name: name?.trim(),
    adEn: adEn?.trim() ?? '',
    extra: extra?.trim() || null,
    parentId: Number(parentId) || 0,
    isImage: isImage ? 1 : 0,
    activeFuarNeeded: activeFuarNeeded ? 1 : 0,
  };
}

export async function createDosyaTuru(form) {
  try {
    return sonuc(await axios.post(FILE_API, dosyaTuruGovdesi(form), { headers: getAuthHeader() }));
  } catch (error) {
    return parseError(error);
  }
}

export async function updateDosyaTuru(id, form) {
  try {
    return sonuc(await axios.put(`${FILE_API}/${id}`, dosyaTuruGovdesi(form), { headers: getAuthHeader() }));
  } catch (error) {
    return parseError(error);
  }
}

export async function deleteDosyaTuru(id) {
  try {
    return sonuc(await axios.delete(`${FILE_API}/${id}`, { headers: getAuthHeader() }));
  } catch (error) {
    return parseError(error);
  }
}
